import { Card, Button, Badge } from "react-bootstrap";
import ProductActiveToggle from "./ProductActiveToggle";

function AdminProductCard({ product, updateProduct, setProducts }) {
  const { name, description, price, isActive } = product;

  // Format price to Philippine Peso
  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(price);
  };

  return (
    <Card
      className={`h-100 admin-product-card ${
        !isActive ? "inactive-product" : ""
      }`}
      style={{
        borderRadius: "20px",
        overflow: "hidden",
        border: "none",
        backgroundColor: isActive ? "#ffffff" : "#f8f9fa",
        boxShadow: isActive
          ? "0 8px 30px rgba(0, 0, 0, 0.12)"
          : "0 4px 15px rgba(0, 0, 0, 0.08)",
        transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
      }}
    >
      {/* Status Badge and Toggle */}
      <div
        className="position-absolute d-flex gap-2 align-items-center"
        style={{ top: "15px", right: "15px", zIndex: 10 }}
      >
        <ProductActiveToggle setProducts={setProducts} product={product} />
        <Badge
          bg={isActive ? "success" : "secondary"}
          className="px-3 py-2"
          style={{
            borderRadius: "25px",
            fontSize: "0.75rem",
            fontWeight: "600",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
          }}
        >
          {isActive ? "Active" : "Inactive"}
        </Badge>
      </div>

      {/* Product Image Placeholder */}
      <div
        className="card-img-top d-flex align-items-center justify-content-center position-relative"
        style={{
          height: "220px",
          background: isActive
            ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            : "linear-gradient(135deg, #bdc3c7 0%, #95a5a6 100%)",
        }}
      >
        {/* Decorative Pattern Overlay */}
        <div
          className="position-absolute w-100 h-100"
          style={{
            background:
              'url("data:image/svg+xml,<svg xmlns=\\"http://www.w3.org/2000/svg\\" viewBox=\\"0 0 100 100\\"><defs><pattern id=\\"adminCardPattern\\" width=\\"20\\" height=\\"20\\" patternUnits=\\"userSpaceOnUse\\"><circle cx=\\"10\\" cy=\\"10\\" r=\\"1\\" fill=\\"white\\" opacity=\\"0.1\\"/></pattern></defs><rect width=\\"100\\" height=\\"100\\" fill=\\"url(%23adminCardPattern)\\"/></svg>")',
            opacity: 0.3,
          }}
        ></div>

        {/* Product Icon */}
        <div className="text-white text-center position-relative z-index-1">
          <div
            className="mb-2"
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              width: "80px",
              height: "80px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
            }}
          >
            <i
              className="fas fa-box"
              style={{ fontSize: "2.5rem", opacity: 0.9 }}
            ></i>
          </div>
          {/* <div style={{ fontSize: "0.85rem", opacity: 0.8, fontWeight: "500" }}>
            Admin Product
          </div> */}
        </div>
      </div>

      <Card.Body className="d-flex flex-column p-4">
        {/* Product Name */}
        <Card.Title
          className={`fw-bold mb-3 ${isActive ? "text-dark" : "text-muted"}`}
          style={{
            fontSize: "1.2rem",
            minHeight: "2.4rem",
            lineHeight: "1.2",
          }}
        >
          {name}
        </Card.Title>

        {/* Product Description */}
        <Card.Text
          className="text-muted flex-grow-1 mb-4"
          style={{
            fontSize: "0.9rem",
            lineHeight: "1.5",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {description}
        </Card.Text>

        {/* Price Section */}
        <div className="mt-auto">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <span
                className={`fw-bold d-block ${
                  isActive ? "text-primary" : "text-muted"
                }`}
                style={{ fontSize: "1.4rem" }}
              >
                {formatPrice(price)}
              </span>
              <small className="text-muted">per kg</small>
            </div>
            <div className="text-end">
              <small className="text-muted d-block">Product ID</small>
              <code className="text-secondary" style={{ fontSize: "0.75rem" }}>
                {product._id?.slice(-6) || "N/A"}
              </code>
            </div>
          </div>

          {/* Admin Action Buttons */}
          <div className="d-grid gap-2">
            <Button
              onClick={() => updateProduct(product)}
              variant={isActive ? "primary" : "outline-secondary"}
              size="sm"
              className="fw-semibold"
              style={{
                borderRadius: "12px",
                padding: "10px 16px",
                transition: "all 0.2s ease",
                border: isActive ? "none" : "2px solid #dee2e6",
              }}
            >
              <i className="fas fa-edit me-2"></i>
              {isActive ? "Edit Product" : "Edit & Activate"}
            </Button>
          </div>
        </div>
      </Card.Body>

      {/* Enhanced Hover Effect Styles */}
      <style jsx>{`
        .admin-product-card {
          transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
          cursor: pointer;
        }

        .admin-product-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
        }

        .inactive-product {
          filter: grayscale(30%) brightness(0.95);
          opacity: 0.85;
        }

        .inactive-product:hover {
          filter: grayscale(15%) brightness(1);
          opacity: 0.95;
        }

        .admin-product-card .btn {
          transition: all 0.2s ease;
        }

        .admin-product-card .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .admin-product-card .position-absolute .badge {
          transition: all 0.2s ease;
        }

        .admin-product-card:hover .position-absolute .badge {
          transform: scale(1.05);
        }

        @media (max-width: 768px) {
          .admin-product-card:hover {
            transform: translateY(-4px);
          }
        }
      `}</style>
    </Card>
  );
}

export default AdminProductCard;
